// src/AuthContext.jsx  —— Unified export form to avoid Vite Fast-Refresh errors
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import { listenAuthChange, getUserInfo } from "./firebase";

/* ------------------------------------------------------------------
 🌐 Auth Context
------------------------------------------------------------------- */
const AuthCtx = createContext({ user: null, loading: true });

/* ================================================================
 * 1⃣  Provider: Monitor Firebase Auth → Validate Whitelist → Inject Globally
 * ============================================================== */
export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);   // {uid,email,role,allow}
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log("[AuthContext] Starting to monitor authentication status changes");
    
    const unsub = listenAuthChange(async (fbUser) => {
      console.log("[AuthContext] Firebase Auth status change:", {
        hasUser: !!fbUser,
        uid: fbUser?.uid,
        email: fbUser?.email
      });
      
      if (!fbUser) {
        console.log("[AuthContext] User logged out, clearing status");
        setUser(null);
        setLoading(false);
        return;
      }
      
      try {
        console.log("[AuthContext] Starting to get user info, UID:", fbUser.uid);
        const info = await getUserInfo(fbUser.uid, fbUser.email);
        console.log("[AuthContext] User info retrieved successfully:", {
          uid: info.uid,
          email: info.email,
          role: info.role,
          allow: info.allow
        });
        setUser(info);                // ✅ Whitelist passed
      } catch (error) {
        console.error("[AuthContext] Failed to get user info:", error);
        setUser(null);                // ❌ Not in whitelist
      } finally {
        setLoading(false);
        console.log("[AuthContext] Status update completed, loading:", false);
      }
    });
    
    return () => {
      console.log("[AuthContext] Cleaning up authentication listener");
      unsub();
    };
  }, []);

  /* Memoization: Context value address is stable, can reduce re-renders */
  const ctxVal = useMemo(() => ({ user, loading }), [user, loading]);

  // Add debug logs
  useEffect(() => {
    console.log("[AuthContext] Status update:", {
      loading,
      hasUser: !!user,
      userInfo: user ? { uid: user.uid, email: user.email, role: user.role } : null
    });
  }, [user, loading]);

  console.log("[AuthContext] Rendering app, user status:", user ? "Logged in" : "Not logged in");
  return <AuthCtx.Provider value={ctxVal}>{children}</AuthCtx.Provider>;
}

/* ================================================================
 * 2⃣  useAuth: Keep "const variable" export, don't change to function declaration,
 *               this way the export signature remains constant during hot updates, HMR won't warn.
 * ============================================================== */
export const useAuth = () => useContext(AuthCtx);

/* 向下兼容：默认导出 Provider（方便外部 import AuthProvider from '…'） */
export default AuthProvider;
